import React, { Fragment } from 'react';
import { Collapse, Stack, TableBody, TableCell, TableRow, useTheme } from '@mui/material';
import _ from 'lodash';
import { columnType, DataType, dataType, tableState } from '../types';
import { format, isValid } from 'date-fns';

interface IProps {
	data: dataType[];
	columns: columnType[];
	tableState: tableState;
	hideHeader?: boolean;
	rowsClass?: string;
	rowAction?: (row: dataType) => void;
	dateFormat?: string;
	noShrink?: boolean;
	/** Optional: return style object for each row (e.g. backgroundColor for duplicate highlighting). Can include sx selectors like &:hover */
	getRowStyle?: (row: dataType) => React.CSSProperties | Record<string, unknown> | undefined;
}

export const BasicTableBody: React.FC<IProps> = ({
	data,
	rowsClass,
	rowAction,
	columns,
	tableState,
	hideHeader,
	dateFormat,
	noShrink,
	getRowStyle,
}: IProps) => {
	const theme = useTheme();

	const formatValue = (type: DataType, value: any, col: columnType, reccord: dataType) => {
		switch (type) {
			case 'custom':
				if (col.customValueHandle) {
					return col.customValueHandle(value, reccord);
				}
				return value;
			case 'date':
				return !_.isNil(value) && isValid(new Date(value))
					? format(new Date(value), dateFormat || 'dd.MM.yyyy HH:mm')
					: '';
			case 'integer':
				return _.isNumber(value) ? Number(value.toFixed(2).replace(/\.00$/, '')).toLocaleString() : value;
			default:
				return value || '';
		}
	};

	const getCustomRowClass = (isOpen: boolean) => {
		let customRowClass = 'BasicTable-tableRow';
		customRowClass = rowsClass ? `${customRowClass} ${rowsClass}` : customRowClass;
		customRowClass = rowAction ? customRowClass + ' rowAction' : customRowClass;
		customRowClass =
			rowsClass === 'collapse'
				? isOpen
					? customRowClass + ' collapse-open'
					: customRowClass + ' collapse-close'
				: customRowClass;
		return customRowClass;
	};

	const [openRows, setOpenRows] = React.useState<Record<number, boolean>>({});

	return (
		<TableBody>
			{data.map((row, i) => {
				return (
					<Fragment key={i + 10000}>
						<TableRow
							key={i}
							onClick={() => {
								if (rowAction) {
									rowAction(row);
								}
								if (rowsClass === 'collapse') {
									setOpenRows((prevOpenRows: any) => ({
										...prevOpenRows,
										[i]: !prevOpenRows[i],
									}));
								}
							}}
							className={getCustomRowClass(openRows[i])}
							sx={getRowStyle?.(row)}
						>
							{columns.map((col, inx) => (
								<TableCell
									{...col.eventsHandle}
									id={String(_.get(row, col.fieldId))}
									key={inx + i + 'z'}
									component="th"
									scope="row"
									className="BasicTable-tableCell"
									sx={{ color: theme.palette.common.black }}
								>
									{!hideHeader ? (
										<Stack direction={'row'}>
											{/* in case mobile*/}
											<span style={{ display: 'none' }}>{col.title}</span>
											{/* in case mobile*/}
											<span className="BasicTable-tableText">
												{formatValue(col.type, _.get(row, col.name), col, row)}
											</span>
										</Stack>
									) : (
										<span className="BasicTable-tableText">
											{formatValue(col.type, _.get(row, col.name), col, row)}
										</span>
									)}
								</TableCell>
							))}
							{!noShrink && <TableCell width="100%"></TableCell>}
						</TableRow>
						{'collapse' === rowsClass && (
							<TableRow key={i + 5000}>
								<TableCell
									colSpan={columns?.length}
									sx={{ display: openRows[i] ? 'table-cell' : 'none' }}
								>
									<Collapse in={openRows[i]} unmountOnExit>
										<span>{String(row?.collapseData)}</span>
									</Collapse>
								</TableCell>
							</TableRow>
						)}
					</Fragment>
				);
			})}
		</TableBody>
	);
};

export default BasicTableBody;
